import { useEffect } from "react";

import { isIPv4 } from "is-ip";

import { useLocationMutation } from "@/pages/Private/redux/account/account.api";

import type { User } from "@/pages/Public/pages/Login/schema/login";

type IpLocationUser = Pick<User, "timezone">;

const IPIFY_URL = "https://api.ipify.org?format=json";

/** The server ignores a location update once the user has a timezone (users.service.ts:380). */
function needsIpLocation(user: IpLocationUser | null | undefined): boolean {
	return !user?.timezone;
}

/** Fills a user's missing location from their IPv4 address without surfacing ipify failures. */
export function useIpLocationSync(user: IpLocationUser | null | undefined): void {
	const [updateLocation] = useLocationMutation();

	useEffect(() => {
		if (!needsIpLocation(user)) {
			return;
		}

		const syncLocation = async () => {
			try {
				const response = await fetch(IPIFY_URL);
				const data: unknown = await response.json();

				if (typeof data !== "object" || data === null || !("ip" in data)) {
					return;
				}

				const { ip } = data;

				if (typeof ip === "string" && isIPv4(ip)) {
					await updateLocation({ ip });
				}
			} catch {
				// Ad blockers and malformed ipify responses need no user-facing error or Sentry event.
			}
		};

		void syncLocation();
	}, [updateLocation, user?.timezone]);
}
